import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ellipsisApi as api, Booking, NewBooking } from '../ellipsisApi';
import { RootState } from '../store';

// The date/dateRange a user has picked on the main booking form for a given
// bookable, tracked independently of whether that pick ever became a synced
// Booking. A synced Booking requires a fully valid selection (e.g. an
// Experience session, not just a date) - but the "Start a Chat" hand-off
// should still show whatever date was picked even when no valid session was
// ever selected (EM-1089). Keyed by bookableId since a page only books one
// bookable at a time, but multiple could coexist in memory.
export interface SelectedBookingDate {
  date: string | null;
  dateRange: [string | null, string | null];
}

interface CheckoutState {
  currentBookings: Booking[];
  totalAmount: number;
  selectedDates: Record<string, SelectedBookingDate>;
}

const initialState: CheckoutState = {
  currentBookings: [],
  totalAmount: 0,
  selectedDates: {},
};

export const updateBooking = createAsyncThunk(
  'booking/update',
  async (payload: NewBooking | null, { dispatch }) => {
    if (payload) {
      const result = dispatch(
        api.endpoints.postBookingCalculateTotals.initiate({
          newBooking: payload,
        })
      );
      (result as any).unsubscribe();
      const response = await result;
      return response.data;
    }
    return payload;
  }
);

const booking = createSlice({
  name: 'booking',
  initialState: initialState,
  reducers: {
    setSelectedBookingDate: (
      state,
      { payload }: PayloadAction<SelectedBookingDate & { bookableId: string }>
    ) => {
      const { bookableId, date, dateRange } = payload;
      if (!bookableId) return;
      state.selectedDates[bookableId] = { date, dateRange };
    },
    // A synced Booking only reflects the session that was valid at the time
    // it synced. If the user then changes the date/session on the main form
    // to something no longer valid (or not yet re-synced), that stale
    // Booking must stop being treated as "the current selection" - without
    // this, reopening the chat form (or the main form itself) keeps
    // reflecting the old date/session instead of the new pick.
    clearCurrentBooking: (state, { payload }: PayloadAction<string>) => {
      const index = state.currentBookings.findIndex(
        (booking) => booking.bookableId === payload
      );
      if (index !== -1) {
        state.currentBookings.splice(index, 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateBooking.fulfilled, (state, { payload }) => {
      if (payload) {
        const index = state.currentBookings.findIndex(
          (booking) => booking.bookableId === payload.bookableId
        );
        if (index === -1) {
          state.currentBookings.push(payload);
        } else {
          state.currentBookings.splice(index, 1);
          state.currentBookings.push(payload);
        }
      }
    });
    builder.addMatcher(
      api.endpoints.postBookings.matchFulfilled,
      (state, { payload }) => {
        const index = state.currentBookings.findIndex(
          (booking) => booking.bookableId === payload.bookableId
        );
        if (index === -1) {
          state.currentBookings.push(payload);
        } else {
          state.currentBookings.splice(index, 1);
          state.currentBookings.push(payload);
        }
      }
    );
    builder.addMatcher(
      api.endpoints.patchBookingsById.matchFulfilled,
      (state, { payload }) => {
        const index = state.currentBookings.findIndex(
          (booking) => booking.bookableId === payload.bookableId
        );
        if (index === -1) {
          state.currentBookings.push(payload);
        } else {
          state.currentBookings.splice(index, 1);
          state.currentBookings.push(payload);
        }
      }
    );
  },
});

export const selectCurrentBookings = (state: RootState) =>
  state.booking.currentBookings;

export const selectSelectedBookingDate = (
  state: RootState,
  bookableId: string | undefined
): SelectedBookingDate | undefined =>
  bookableId ? state.booking.selectedDates[bookableId] : undefined;

export const { setSelectedBookingDate, clearCurrentBooking } = booking.actions;

export default booking;
